import { useTranslations } from '@vocab/react';
import { Box, TextField, IconSearch } from 'braid-design-system';
import {
  useState,
  type ChangeEvent,
  type FC,
  type FormEvent,
  type PropsWithChildren,
} from 'react';

import { MAX_SEARCH_TEXT_LENGTH } from 'src/App/constants/filter';
import { useQueryParam } from 'src/hooks/useQueryParam';

import translations from './.vocab';

const isMaxLengthExceeded = (searchText?: string): boolean =>
  (searchText?.length ?? 0) > MAX_SEARCH_TEXT_LENGTH;

export const SearchBar: FC<PropsWithChildren<unknown>> = () => {
  const { t } = useTranslations(translations);
  const { q, setQ } = useQueryParam();
  const [search, setSearch] = useState(q);
  const [hasError, setHasError] = useState(isMaxLengthExceeded(q));

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasError(isMaxLengthExceeded(event.target.value));
    setSearch(event.target.value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (hasError) {
      return;
    }

    setQ(search);
  };

  const onClear = () => {
    setQ('');
    setSearch('');
    setHasError(false);
  };

  return (
    <Box
      width="full"
      component="form"
      onSubmit={onSubmit}
      data={{ testid: 'searchbar', intercomid: 'searchBar' }}
    >
      <TextField
        id="search"
        aria-label={t('Job Search Text Input')}
        type="text"
        value={search ?? ''}
        onChange={onInputChange}
        onClear={onClear}
        placeholder={t('Search job title or reference number')}
        icon={<IconSearch />}
        characterLimit={80}
        tone={hasError ? 'critical' : 'neutral'}
        message={
          hasError
            ? t('Search text needs to be ...', {
                maxCharacters: MAX_SEARCH_TEXT_LENGTH,
              })
            : undefined
        }
      />
    </Box>
  );
};
